import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { KIND_LABELS } from "@/lib/workKinds";
import { LIMIT, sendTitle, sendSub, todayLine, sendsLine, overLine, sentLine, statusWord } from "@/lib/documents";
import { CircleCheck, Check, X, Send, CalendarClock } from "lucide-react";

// design_handoff_founder_assistant screen 5 (SendDialog.dc.html): the Send dialog for a document that's ready (or sent:
// Send again). From the founder "via Crewbench", replies to them; to the investors, team members and addresses they
// pick, each their own email (backend doc_send.py); at most 50 a day for the company. They saw the preview: Send sends,
// with no more confirming. Schedule opens the scheduler (Scheduler.jsx) with the same people.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UPPER = "text-xs uppercase tracking-[0.09em] text-muted-foreground";

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-2 items-baseline">
      <span className="text-sm text-muted-foreground">{label}</span><span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

export default function SendDocument({ doc, open, onOpenChange, onDocuments }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: setup } = useQuery({ queryKey: ["documents-sending"], enabled: open,
                                     queryFn: async () => (await api.get("/documents/sending")).data });
  const { data: full } = useQuery({ queryKey: ["document", doc?.id], enabled: open && !!doc,
                                    queryFn: async () => (await api.get(`/documents/${doc.id}`)).data.document });
  const [to, setTo] = useState([]);
  const [subject, setSubject] = useState("");
  const [typed, setTyped] = useState("");
  const [listing, setListing] = useState(false);
  const [sent, setSent] = useState(null);
  const field = useRef(null);
  useEffect(() => {
    if (!open) return;
    setTo((doc?.recipients || []).filter((r) => r.email));
    setTyped(""); setListing(false); setSent(null);
  }, [open, doc?.id]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && full) setSubject(full.subject || ""); }, [open, full?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const send = useMutation({
    mutationFn: async () => (await api.post(`/documents/${doc.id}/send`, { recipients: to, subject: subject.trim() })).data,
    onSuccess: (d) => {
      setSent(d);
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["document", doc.id] });
      qc.setQueryData(["documents-sending"], (old) => old && { ...old, sent_today: d.sent_today });
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!doc) return null;

  const has = (email) => to.some((r) => r.email.toLowerCase() === email.toLowerCase());
  const add = (person) => { if (!has(person.email)) setTo([...to, person]); setTyped(""); };
  const drop = (email) => setTo(to.filter((r) => r.email.toLowerCase() !== email.toLowerCase()));
  const toggle = (person) => (has(person.email) ? drop(person.email) : add(person));
  const addTyped = () => {
    const email = typed.trim();
    if (!email) return;
    if (!EMAIL.test(email)) { toast.error("Type an email address."); return; }
    const known = [...(setup?.investors || []), ...(setup?.team || [])].find((p) => p.email.toLowerCase() === email.toLowerCase());
    add(known || { name: "", email, kind: "address" });
  };
  const today = setup?.sent_today ?? 0;
  const limit = setup?.limit ?? LIMIT;
  const over = overLine(today, to.length, limit);
  const ready = setup?.email && to.length > 0 && subject.trim() && !over;
  const q = typed.trim().toLowerCase();
  const match = (p) => !q || p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] gap-[18px]" data-testid="send-dialog">
        <div className="flex flex-col gap-1">
          <DialogTitle>{sendTitle(doc)}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{sendSub(doc)}</DialogDescription>
        </div>
        {sent ? (
          <div className="flex flex-col gap-3" data-testid="send-done">
            <span className="flex items-start gap-2.5">
              <CircleCheck className="w-5 h-5 mt-0.5 shrink-0 text-gr-success" aria-hidden="true" />
              <span className="flex flex-col gap-0.5">
                <span className="font-semibold">{statusWord({ status: "sent", sent_count: sent.document.sent_to.length })}</span>
                <span className="text-sm text-muted-foreground">{sentLine(sent.document.sent_to, sent.document.sent_at)}</span>
                <span className="text-sm text-muted-foreground">{todayLine(sent.sent_today, sent.limit)}</span>
              </span>
            </span>
            <div className="flex justify-end gap-2">
              {onDocuments && <Button variant="outline" asChild><Link to="/founder/documents" onClick={() => onOpenChange(false)}>Open Documents</Link></Button>}
              <Button onClick={() => onOpenChange(false)} data-testid="send-done-btn">Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-[18px]" onSubmit={(e) => { e.preventDefault(); if (ready && !send.isPending) send.mutate(); }}>
            <div className="flex flex-col gap-1.5">
              <Row label="From">{setup?.from || "…"}</Row>
              <Row label="Replies go to">{setup?.reply_to || "…"}</Row>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="send-to">To</Label>
              <div className={cn("flex flex-wrap items-center gap-1.5 min-h-8 px-2 py-1 border rounded-md bg-background",
                over ? "border-gr-danger" : "border-gr-control-border")} onClick={() => field.current?.focus()}>
                {to.map((r) => (
                  <span key={r.email} className="inline-flex items-center gap-1 h-6 ps-2 pe-1 rounded-full bg-gr-surface-2 max-w-full" data-testid="send-chip">
                    <span className="truncate">{r.name || r.email}</span>
                    <button type="button" aria-label={`Remove ${r.name || r.email}`} onClick={() => drop(r.email)}
                      className="w-5 h-5 grid place-items-center rounded-full hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                      <X className="w-3 h-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
                <input id="send-to" ref={field} value={typed} onChange={(e) => { setTyped(e.target.value); setListing(true); }}
                  onFocus={() => setListing(true)} placeholder="Add a person or type an address" data-testid="send-to"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); addTyped(); }
                    else if (e.key === "Backspace" && !typed && to.length) drop(to[to.length - 1].email);
                    else if (e.key === "Escape" && listing) { e.stopPropagation(); setListing(false); }
                  }}
                  className="flex-1 min-w-[160px] h-6 border-0 bg-transparent outline-none placeholder:text-muted-foreground" />
              </div>
              {listing && setup && (
                <div className="border border-gr-hairline rounded-md p-2 flex flex-col gap-1 max-h-60 overflow-y-auto" data-testid="send-list">
                  {setup.investors.filter(match).length > 0 && <span className={cn(UPPER, "px-1.5 pt-1")}>Investors</span>}
                  {setup.investors.filter(match).map((p) => (
                    <button key={p.email} type="button" onClick={() => toggle(p)} aria-pressed={has(p.email)}
                      className="flex items-center gap-2 px-1.5 py-1 rounded text-start hover:bg-gr-hover">
                      <span className={cn("w-4 h-4 grid place-items-center rounded-[3px] border", has(p.email) ? "bg-foreground text-background border-foreground" : "border-gr-control-border")}>
                        {has(p.email) && <Check className="w-3 h-3" aria-hidden="true" />}
                      </span>{p.name}
                    </button>
                  ))}
                  {setup.team.filter(match).length > 0 && <span className={cn(UPPER, "px-1.5 pt-1")}>Team</span>}
                  {setup.team.filter(match).map((p) => (
                    <div key={p.email} className="flex items-center gap-2 px-1.5 py-0.5">
                      <span className="flex-1 min-w-0 truncate">{p.name}{KIND_LABELS[p.work_kind] ? ` · ${KIND_LABELS[p.work_kind]}` : ""}</span>
                      <Button type="button" size="sm" variant="outline" disabled={has(p.email)} onClick={() => add(p)}>{has(p.email) ? "Added" : "Add"}</Button>
                    </div>
                  ))}
                  <span className="px-1.5 pt-1 text-sm text-muted-foreground">Independent professionals aren't in this list. Type an address to add anyone else.</span>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="send-subject">Subject</Label>
              <Input id="send-subject" value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} data-testid="send-subject" />
            </div>
            <div className="flex flex-col gap-1.5" data-testid="send-count">
              <span className="flex flex-wrap justify-between gap-2 text-sm">
                <span className={cn(over ? "text-gr-danger" : "text-muted-foreground")}>{todayLine(today, limit)}</span>
                <span className="text-muted-foreground">{sendsLine(to.length)}</span>
              </span>
              <span className="block h-1 rounded-sm bg-gr-hairline overflow-hidden" aria-hidden="true">
                <span className={cn("block h-full", over ? "bg-gr-danger" : "bg-foreground")} style={{ width: `${Math.min(100, (100 * (today + to.length)) / limit)}%` }} />
              </span>
              {over && <span className="text-sm text-gr-danger" role="alert">{over}</span>}
              {setup && !setup.email && <span className="text-sm text-gr-warning" role="alert">Email isn't set up yet, so nothing can be sent.</span>}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              {doc.type === "investor_update" && (
                <Button type="button" variant="outline" data-testid="send-schedule"
                  onClick={() => { onOpenChange(false); navigate("/founder/documents/schedule", { state: { recipients: to } }); }}>
                  <CalendarClock />Schedule
                </Button>
              )}
              <Button type="submit" aria-disabled={!ready} disabled={send.isPending} data-testid="send-btn"><Send />Send</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
